import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslations } from "use-intl";
import style from "@/components/Dropdown/index.module.scss";
import { CInput } from "@/components/Input/CInput";
import { CurrencySidebar } from "@/components/CurrencySidebar";
import { RootState, useAppDispatch } from "@/store";
import {
  setCurrentSubAccount,
  setCurrentSubAccountInfo,
  setShowModal,
  setStatisticsSubAccount,
} from "@/store/sub_accounts";
import { CURRENT_SUBACCOUNT_KEY } from "@/variables";
import viewStyle from "./sidebar.module.scss";

const STATISTICS_URL = "/cabinet/statistics";

// "Просмотр": what the dashboard shows - all assets, one currency, several
// ticked sub-accounts of a currency, or one sub-account. Picking a single
// sub-account also makes it the current one for the other cabinet pages,
// as the old "Subaccounts" dropdown did.
export function ViewSelector() {
  const i18n = useTranslations("cabinet");
  const sidebarI18n = useTranslations("cabinet.sidebar");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const {
    subAccounts,
    currentSubAccount,
    statisticsViewMode,
    statisticsCurrency,
    statisticsSelection,
  } = useSelector((state: RootState) => state.subAccount);
  const onStatistics = pathname.startsWith(STATISTICS_URL);

  useEffect(() => {
    if (!opened) {
      return;
    }
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpened(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [opened]);

  let value: React.ReactNode = currentSubAccount;
  if (onStatistics) {
    if (statisticsViewMode === "portfolio") {
      value = i18n("statistics.allAssets");
    } else if (statisticsViewMode === "aggregate") {
      value = statisticsSelection.length
        ? `${statisticsCurrency}: ${i18n("statistics.selectedCount", {
            count: statisticsSelection.length,
          })}`
        : `${i18n("statistics.allOf")} ${statisticsCurrency}`;
    } else {
      value = `${statisticsCurrency} / ${currentSubAccount}`;
    }
  }

  const pickSubAccount = (name: string, currency: string) => {
    const account = subAccounts.find((acc: any) => acc.name === name);
    localStorage.setItem(CURRENT_SUBACCOUNT_KEY, JSON.stringify(account || {}));
    dispatch(setCurrentSubAccount(name));
    dispatch(setCurrentSubAccountInfo(account));
    if (onStatistics) {
      dispatch(setStatisticsSubAccount({ name, currency }));
    }
    setOpened(false);
  };

  return (
    <div className={style.dropdown} ref={ref}>
      <div
        className={classNames(style.dropdown__btn, opened ? style.active : "")}
        onClick={() => setOpened((val) => !val)}
      >
        <div className={style.dropdown__btn__content}>
          <span>{sidebarI18n("view")}</span>
          <span>{value}</span>
        </div>
        <div className={style.dropdown__btn__image}>
          <img
            src={require("@/images/main/bottom-arrow-white.svg").default}
            alt={"arrow down"}
          />
        </div>
      </div>
      {opened && (
        <div className={classNames(style.dropdown__content, viewStyle.viewSelector)}>
          <div className={style.dropdown__content__heading}>
            <CInput
              error={""}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={sidebarI18n("search")}
            />
          </div>
          <div
            className={viewStyle.viewSelector__create}
            onClick={() => {
              dispatch(setShowModal(true));
              setOpened(false);
            }}
          >
            {sidebarI18n("create")}
          </div>
          <div className={viewStyle.viewSelector__tree}>
            <CurrencySidebar
              filter={search}
              onSubAccountPick={pickSubAccount}
              onViewChange={(done) => {
                if (!onStatistics) {
                  navigate(STATISTICS_URL);
                }
                if (done) {
                  setOpened(false);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
